// src/components/PeoplePlan/EmployeeManagement/WorkflowPanel.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../../apiClient";
import DynamicWorkflowForm from "./WorkflowForms/DynamicWorkflowForms";
import {
  getWorkflowOptions,
  getWorkflowByName,
} from "../configs/workflowRegistry";
import styles from "./GenericEmployeeTable.module.css";

const WorkflowPanel = ({
  selectedWorkflow,
  workflowDescription,
  onWorkflowChange,
  formData,
  formErrors,
  onFormFieldChange,
  onStartWorkflow,
  onClosePanel,
  isSubmitting,
  customWorkflows = null,
  workflowMode = null,
  roleConfig = null,
}) => {
  const navigate = useNavigate();
  const [storeList, setStoreList] = useState([]);

  // Fetch store list for transfer workflow
  useEffect(() => {
    if (
      selectedWorkflow === "10" || // Check for Transfer workflow ID
      workflowDescription?.formFields?.some((f) => f.type === "store-select")
    ) {
      fetchStoreList();
    }
  }, [selectedWorkflow, workflowDescription]);

  const fetchStoreList = async () => {
    try {
      const response = await apiClient.get("/api/store-list");
      setStoreList(response.data);
    } catch (error) {
      console.error("Error fetching store list:", error);
    }
  };

  // Get training position options based on current role
  const getTrainingPositionOptions = () => {
    if (!roleConfig) return [];

    const role = roleConfig.singularName || roleConfig.displayName;

    switch(role) {
      case 'Crew Member':
      case 'Crew Members':
        return ['Shift Leader', 'Assistant Manager', 'Restaurant Manager'];
      case 'Shift Leader':
      case 'Shift Leaders':
        return ['Assistant Manager', 'Restaurant Manager'];
      case 'Assistant Manager':
      case 'Assistant Managers':
        return ['Restaurant Manager', 'MUM'];
      case 'Restaurant Manager':
      case 'Restaurant Managers':
        return ['MUM'];
      default:
        return [];
    }
  };

  // Filter workflows based on mode
  const getAvailableWorkflows = () => {
    const allWorkflows = customWorkflows || getWorkflowOptions();

    if (workflowMode === "position") {
      // Only show Request Position workflow for empty positions
      return allWorkflows.filter((w) => w === "Request Position");
    } else if (workflowMode === "employee") {
      // Show all workflows EXCEPT Request Position for employees
      // Position Training is available for employees
      return allWorkflows.filter((w) => w !== "Request Position");
    } else {
      // Default: show all workflows
      return allWorkflows;
    }
  };

  const workflowOptions = getAvailableWorkflows();

  const handleWorkflowSelect = (e) => {
    const selectedName = e.target.value;
    if (!selectedName) {
      onWorkflowChange("");
      return;
    }

    const workflow = getWorkflowByName(selectedName);
    if (workflow) {
      onWorkflowChange(workflow.id.toString());

      // Don't auto-populate for workflow 12, let user select
      if (workflow.id === 12) {
        // Clear any existing selection
        onFormFieldChange("trainingPosition", "");
      }
    } else {
      onWorkflowChange("");
    }
  };

  return (
    <div className={styles.benchSection}>
      <div className={styles.benchHeader}>
        <h3>
          Workflows
          {workflowMode === "position" && (
            <span className={styles.benchModeIndicator}>
              {" "}
              - Position Request
            </span>
          )}
          {workflowMode === "employee" && selectedWorkflow === "12" && (
            <span className={styles.benchModeIndicator}>
              {" "}
              - Position Training
            </span>
          )}
        </h3>
        <button
          className={styles.closeButton}
          onClick={onClosePanel}
          aria-label="Close workflow panel"
          title="Close"
        >
          ×
        </button>
      </div>

      <div className={styles.workflowSelector}>
        <select
          value={workflowDescription?.displayName || ""}
          onChange={handleWorkflowSelect}
        >
          <option value="">
            {workflowMode === "position"
              ? "Request Position (Auto-Selected)"
              : "Select Workflow"}
          </option>
          {workflowOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

        {workflowMode === "position" &&
          workflowOptions.length === 1 &&
          !workflowDescription && (
            <div className={styles.workflowHint}>
              Select "Request Position" to continue with vacant position request
            </div>
          )}

        {workflowDescription && (
          <div className={styles.workflowDetails}>
            <p className={styles.workflowDescription}>
              {workflowDescription.description}
            </p>

            <div className={styles.workflowSteps}>
              {workflowDescription.steps.map((step, index) => (
                <div key={index} className={styles.workflowStep}>
                  <span className={styles.stepBullet}>•</span>
                  <span className={styles.stepText}>{step}</span>
                </div>
              ))}
            </div>

            {workflowDescription.requiresForm && (
              <DynamicWorkflowForm
                workflow={workflowDescription}
                formData={formData}
                formErrors={formErrors}
                onFieldChange={onFormFieldChange}
                storeList={storeList}
                dynamicOptions={selectedWorkflow === "12" ? { trainingPosition: getTrainingPositionOptions() } : null}
              />
            )}

            {/* Navigation workflow button */}
            {workflowDescription.isNavigationWorkflow ? (
              <button
                className={styles.startWorkflowButton}
                onClick={() => navigate(workflowDescription.navigationPath)}
              >
                Go to {workflowDescription.displayName.replace("Create ", "")}
              </button>
            ) : (
              <button
                className={styles.startWorkflowButton}
                onClick={onStartWorkflow}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Processing..."
                  : workflowMode === "position"
                  ? "Submit Position Request"
                  : selectedWorkflow === "12"
                  ? "Start Position Training"
                  : workflowDescription.isMeetingWorkflow
                  ? "Schedule Meeting"
                  : workflowDescription.isPeoplePulseWorkflow
                  ? `Submit ${workflowDescription.displayName.replace("Create ", "")}`
                  : "Start Workflow"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowPanel;
